<script lang="ts">
import type { MaterialTileProps } from "@/types/partners.types";

export default {
  name: "MaterialTile",
};
</script>

<script setup lang="ts">
withDefaults(defineProps<MaterialTileProps>(), {
  isAuthor: false,
  imageUrl: undefined,
  rounded: false,
});

const { d } = useI18n();
</script>

<template>
  <div class="material-tile">
    <div
      class="material-tile__color-bar"
      :style="`border-color: ${color}; background-color: ${color}`"
    ></div>
    <div class="material-tile__header">
      <div
        class="material-tile__bg"
        :class="{ 'material-tile__bg--rounded': rounded }"
      >
        <LazyImage
          v-if="imageUrl"
          class="material-tile__bg-image"
          :src="imageUrl"
          alt="avatar"
        />
        <i
          v-else
          class="material-tile__bg-icon"
          :class="{
            [`bi ${icon}`]: true,
            'material-tile__bg-icon--rounded': rounded,
          }"
          :style="`color: ${color}`"
        />
      </div>
    </div>

    <div class="material-tile__kind" :style="`border-color: ${color}`">
      <span class="material-tile__icon"><i :class="`bi ${icon}`"></i></span>
      <span>{{ type }}</span>
    </div>

    <div class="material-tile__content">
      <div class="material-tile__title">
        <a :href="url" class="stretched-link" data-testid="link-material">
          {{ name }}</a
        >
      </div>
      <div class="material-tile__footer">
        <div class="material-tile__author">
          <slot name="author" />
        </div>
        <div class="d-none d-sm-flex text-muted font-xs">
          {{ d(new Date(date), "numeric") }}
        </div>
        <div class="material-tile__actions">
          <slot name="actions" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.material-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1;
  height: 21rem;
  overflow: hidden;
  border-radius: $border-radius;
  background: $color-white;
  color: $color-black;
  text-decoration: none;
  transition: transform 0.1s ease-out;
  box-shadow: $brand-box-shadow;

  &:hover {
    transform: scale(1.03);
  }

  @include media-breakpoint-down(sm) {
    flex-direction: row;
    height: 8rem;
    border-radius: $border-radius-md;
  }

  &__color-bar {
    background: $gray-200;
    height: $spacer-2;
    border-bottom: $spacer-2 solid $gray-200;
    width: 100%;

    @include media-breakpoint-down(sm) {
      display: none;
    }
  }

  &__header {
    display: flex;
    justify-content: flex-end;
    align-items: start;
    overflow: hidden;
    width: 100%;
    height: 11rem;
    background-color: $gray-100;

    @include media-breakpoint-down(sm) {
      height: 100%;
      width: 100%;
      max-width: 4.85rem;
      max-height: 8.3125rem;
    }

    @include media-breakpoint-down(sm) {
      &:has(.material-tile__bg-icon-wrapper--assistant) {
        display: block;
      }
    }
  }

  &__bg {
    position: relative;
    top: 1.25rem;
    right: -$spacer-5;
    width: 11.25rem;
    height: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    background-color: $color-white;
    border-radius: 1rem;
    overflow: hidden;

    @include media-breakpoint-down(sm) {
      top: $spacer-2;
      left: $spacer-3;
      height: 7rem;
    }

    &--rounded {
      right: -35px;
      width: 12rem;
      height: 12rem;
      border-radius: 50%;

      @include media-breakpoint-down(sm) {
        top: 1.5rem;
        right: 0;
        width: 5rem;
        height: 5rem;
      }
    }
  }

  &__bg-icon {
    position: absolute;
    top: -$spacer-4;
    left: $spacer-5;
    font-size: 9.5rem;

    @include media-breakpoint-down(sm) {
      top: -0.75rem;
      left: 0.25rem;
      font-size: 5.5rem;
    }

    &--rounded {
      left: 1.625rem;
      top: -1.55rem;
      font-size: 8.75rem;

      @include media-breakpoint-down(sm) {
        font-size: 5.5rem;
        left: $spacer-3;
        top: -0.85rem;
      }
    }
  }

  &__bg-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__kind {
    display: flex;
    align-items: center;
    position: absolute;
    top: 1.5rem;
    left: 1.5rem;
    width: fit-content;
    height: 1.75rem;
    border: 2px solid $gray-200;
    border-radius: $border-radius-md;
    padding: $spacer-1 $spacer-2 $spacer-1 0.15rem;
    background-color: $color-white;
    color: $gray-600;
    font-size: $font-size-xs;

    @include media-breakpoint-down(sm) {
      top: 0.5rem;
      left: 5.35rem;
      border: none;
      z-index: 1;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    background: $white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    color: $gray-600;
    font-size: $font-size-xs;
  }

  &__content {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 9.95rem;
    max-height: 100%;
  }

  &__title {
    max-height: 4.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: $spacer-3 $spacer-4 $spacer-2 $spacer-4;
    background: $color-white;

    @include media-breakpoint-down(md) {
      height: fit-content;
    }

    @include media-breakpoint-down(sm) {
      max-height: 3.75rem;
      margin-bottom: $spacer-1;
      margin-top: 1.25rem;
      padding: $spacer-3;
      padding-bottom: $spacer-1;
    }

    a {
      display: -webkit-box;
      line-clamp: 2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      word-wrap: anywhere;
      font-size: $font-size-base;
      font-weight: $font-weight-semibold;
      text-decoration: none;
      color: $color-black;

      @include media-breakpoint-down(sm) {
        font-size: $font-size-sm;
        line-clamp: 2;
        -webkit-line-clamp: 2;
      }
    }
  }

  &__footer {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    justify-content: space-between;
    background: transparent;
    padding: 0 $spacer-4 $spacer-3;

    @include media-breakpoint-down(sm) {
      padding: 0 $spacer-3 0.75rem;
      width: 100%;
      height: fit-content;
    }
  }

  &__author {
    display: flex;
    align-items: center;
    color: $gray-600;
    font-size: $font-size-sm;

    @include media-breakpoint-down(sm) {
      font-size: $font-size-xs;
      margin-top: auto;
    }
  }

  &__actions {
    position: absolute;
    right: $spacer-4;
    bottom: $spacer-3;

    @include media-breakpoint-down(sm) {
      right: $spacer-3;
      bottom: 0.75rem;
    }
  }
}
</style>
